// THE VERDICT THAT SETTLES A RAW TASK — the novelty and escalation-delivery
// vocabularies it carries, the discriminated union itself, and the compile-time proof
// that every `TriageVerdict` has a member in it.
// Split out of `../raw-task.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { BlockerIdSchema, type RawTaskId, TaskIdSchema } from '../../value-objects/ids';
import { ScreenDiscardReasonSchema } from '../../value-objects/intake-screen';
import { TriageVerdict } from '../../value-objects/triage-verdict';
import { RAW_TASK_TASK_IDS_MAX } from './evidence';

/**
 * The decision that took a raw task out of `pending`, stored verbatim in
 * `raw_tasks.verdict`.
 *
 * snake_case, like `TriageVerdictRecord` and `TaskEnvelope`: it crosses into
 * jsonb and into `audit_events.output` unchanged, and one casing convention for
 * "records that leave TypeScript" is what keeps a reader from having to know
 * which side wrote a row.
 *
 * It is NOT `TriageVerdictRecord` (`value-objects/triage-verdict.ts`), and the
 * difference is the point: that record REQUIRES `confidence`, because it attests
 * a judgement over an already-scoped `TaskEnvelope` that carries one. A raw task
 * has no confidence — confidence is triage's output, arrived at by reading the
 * ask against the board, and a settle forced to supply one before it has one
 * would have to invent it. The shared half (`TriageVerdict`, the four members and
 * their meanings) is imported rather than restated, so the vocabulary stays
 * single-sourced.
 *
 * A DISCRIMINATED UNION, not one object plus cross-field refinements, and the
 * difference is what a caller can express rather than what a parse rejects. Under
 * the old shape `{verdict: 'schedule', reason}` TYPE-CHECKED and only failed at
 * `.parse()`; the evidence each verdict owns was optional on all four and policed
 * by three `superRefine` branches. Now `task_ids` and `novelty` exist only on
 * `schedule` and `dupe_of_task_id` only on `dupe-of` — a lane that carried neither
 * is not a runtime refusal, it is a compile error, and the three refinement
 * branches are gone because there is nothing left for them to say.
 *
 * `z.strictObject` on every member, deliberately: a plain `z.object` STRIPS an
 * unknown key, so `{verdict: 'drop-noise', task_ids: [...]}` would have parsed
 * clean with the ids silently discarded — a refusal indistinguishable from
 * nothing-happened, on the one record that has to be trustworthy after the fact.
 */
const VerdictReasonSchema = z.string().min(1).max(200);

/**
 * Whether a `schedule` was allowed to CLAIM the ask was new work.
 *
 * `schedule` is the one verdict that rests on a NEGATIVE — "no live task already
 * covers this" — and the only evidence for it is the account board triage read.
 * That board is capped (`ACCOUNT_BOARD_DEFAULT_LIMIT` 200, max 500) against an
 * account that held 2,303 non-terminal tasks on 2026-08-27, so the cap bites on
 * exactly the org where a duplicate costs most. When it bites, the covering task
 * may be among the rows that were cut, and the absence of a `dupe-of` stops being
 * a finding: it is a question nobody could answer.
 *
 * TWO VALUES, WRITTEN EVERY TIME, because the alternative is the silence this
 * field exists to end. A settled `schedule` that omitted the bit would read
 * identically whether the board was whole or a prefix of it, and a reader counting
 * duplicates could not tell a triage miss from a truncation. It is REQUIRED on the
 * member (see {@link RawTaskVerdictSchema}) and unspellable on the other three: a
 * `dupe-of` asserts a positive against an id it was shown, and `drop-noise` /
 * `escalate` assert nothing about novelty at all.
 *
 * WHO READS IT, stated rather than assumed — this repo's most recurrent defect is a
 * mechanism with no reader. It lands in three durable places off ONE write, because
 * the `schedule` arm hands this same record to all of them: `raw_tasks.verdict`, the
 * `triage.verdict` audit row, and the `raw_task.triaged` settle row. That makes it a
 * query — `select count(*) from raw_tasks where verdict->>'novelty' = 'unproven'` —
 * which is the reader it is for. NO surface renders it, and that is not a gap: no
 * product surface renders a triage verdict at all today, and the one human-facing hop
 * the lane has (`NotificationKind.AskDuplicate`) answers the person whose ask was
 * DROPPED. A `schedule` already answers its asker in the work it minted.
 */
export const TriageNovelty = {
  /**
   * The board was COMPLETE. Every non-terminal task the account held was in front
   * of the judgement, so "nothing covers this" is a finding.
   */
  Checked: 'checked',
  /**
   * The board was TRUNCATED. The schedule stands — the work is real and the pieces
   * are adjudicated — but novelty was NOT established, and nothing downstream may
   * read this row as "we looked and found nothing".
   */
  Unproven: 'unproven',
} as const;
export const TriageNoveltySchema = z.enum(TriageNovelty);
export type TriageNovelty = z.infer<typeof TriageNoveltySchema>;

/**
 * What an `escalate` verdict's DELIVERY LEG actually did — the verdict's own answer
 * to "is a human going to see this".
 *
 * WHY THE FIELD EXISTS. `escalate` means a human decides, and the lane recorded that
 * sentence with nothing behind it. Measured on prod 2026-09-01: **47 of 73 settled
 * raw tasks were `escalate`** and **0 of the 1,421 `escalations` rows came from
 * triage** — every escalation on the platform was fired by a session, none by an
 * ask. `apps/agents-manager`'s own `deliverDupeAnswer` said so in prose while it was
 * false ("a `drop-noise` or an `escalate` is a different message with a different
 * audience — the escalation engine already owns the second"), which is the comment
 * class `CLAUDE.md` names as an architecture signal: a guarantee the code does not
 * provide. So an escalated ask produced a `raw_tasks` row, an audit row, and
 * silence — byte-identical to the platform losing the request, the same observable
 * `dupe-of` got its answer for.
 *
 * WHY NOT {@link EscalationDeliveryState}, which is the sibling vocabulary and the
 * obvious reuse. Its members name what the EMAIL LEG of an `escalations` row did —
 * `delivered`, `collapsed`, `no_route`, `send_failed`, `digest_pending` — and this
 * lane runs none of them. It cannot: `escalations.repo_id` is `NOT NULL` and
 * references `repositories`, while a raw task carries `repo_full_names[]` that may
 * be empty, and there is no session to chain the audit onto. Spelling `no_route` on
 * a verdict whose lane never resolved a route would be the drift surface, not the
 * safeguard. The two vocabularies are deliberately disjoint and this one is
 * SMALLER, because it says only what this lane can observe.
 *
 * | member        | who writes it                                  | what it means |
 * |---------------|------------------------------------------------|---------------|
 * | `pending`     | every settler that TELLS (list below), at the settle (`0396`, #7649) | settled; the notice is not attempted yet. Older than the sweep's grace = the settling pass died before it told anyone or recorded what it told |
 * | `queued`      | the delivery leg, after a won settle, when `notify.fanout` accepted | the platform notification plane owns it from here |
 * | `undelivered` | the delivery leg, after a won settle, when the enqueue faulted; OR a settler that never tells, at the settle (list below) | NOBODY will be told |
 * | `unattempted` | migration `0180`, backfilling pre-hop rows      | nothing — no delivery leg existed yet |
 *
 * EVERY WRITER OF AN `escalate` VERDICT, by whether it tells a human. THE SETTLERS
 * THAT TELL write `pending` and then run `deliverSettledEscalation`
 * (`@developerz/agent`) after a won settle:
 *   - the triage lane's judged escalate and its refusals (`@developerz/agent`
 *     `raw-task-triage.ts`), settled and told by `apps/agents-manager`
 *     `raw-task-triage.ts` (`settleRawTask`, or `settleInfoRoundsExhausted` at the
 *     `needs_info` cap) through `triage-escalation-tell.ts`;
 *   - the refusal settle, `apps/agents-manager` `triage-refusal.ts` `settleRefusal`;
 *   - the admission drain's local repo refusal, `apps/worker`
 *     `consumers/raw-task-bridge.ts`;
 *   - the unanswered-question expiry, `apps/worker` `cron/blocker-expiry.lane.ts`
 *     (`expireUnansweredAskBlocker`).
 * THE SETTLERS THAT NEVER TELL write the terminal `undelivered` directly, because no
 * notice will ever be attempted, and that is the truth the member states:
 *   - a withdrawn question's parked ask, `@developerz/db` `blockers-withdraw.ts`;
 *   - a claimed row that fails its schema, `@developerz/db` `raw-tasks-row-invalid.ts`.
 * Re-check the lists with `grep -rn "TriageEscalationDelivery\." --include=*.ts` outside
 * tests. A new escalate writer joins one of them; `pending` written by a settler that
 * never tells would read as a crashed pass forever.
 *
 * SETTLE, THEN TELL (#7649). The delivery used to run BEFORE the settle so its outcome
 * could ride the settled verdict. Every fault between the tell and the settle (the
 * verdict audit row, the settle announcement, the settle itself, a lapsed lease) left
 * the ask claimed and unsettled, and the re-claim RE-JUDGED it — possibly into
 * `schedule`, after a maintainer had been told triage stopped. Nothing re-judges a
 * settled row, so a settler that tells now settles `pending` and the notice is queued
 * only by the pass whose settle WON, once (`@developerz/agent` `deliverSettledEscalation`).
 * The outcome is then recorded by ONE fenced write, `pending` -> `queued` /
 * `undelivered` (`@developerz/db` `recordRawTaskEscalationDelivery`), in ONE transaction
 * with its `raw_task.escalation_delivered` audit row (#7653). A pass that dies between
 * the settle and that transaction, or a transaction that rolls back, leaves `pending`, which `cron.raw_task_sweep` pages at most once per row
 * (`readStalePendingEscalationDeliveries`; the rows it misses — a tick that never ran,
 * a failed read, more than 50 in one window — are counted, not named, and listed in
 * `pageStaleDeliveries`); it is never re-told, because a
 * duplicate page is not what a crash should buy.
 *
 * `queued` IS NOT `delivered`, and the distinction is the whole point of the split
 * it copies. It attests a HANDOFF: the event reached `notify.fanout` under a dedupe
 * key, and whether a human was actually reached is the notification ledger's answer
 * — `select status from notifications where dedupe_key = 'ask_escalated-<rtk id>'`,
 * which is a real join because `notifications.dedupe_key` is a real column. Naming
 * it `delivered` here would forge exactly the claim `escalations.delivered_at`
 * forged for 1,421 rows.
 *
 * `unattempted` and `undelivered` are deliberately distinct, on the identical
 * reasoning that keeps `EscalationDeliveryState`'s `unknown` apart from its
 * `pending`: `undelivered` means a writer existed and failed, so the row is a live
 * alarm; `unattempted` means the row predates the hop, so it can never be one. It
 * has NO live writer by construction — nothing in the lane can produce it — and
 * that is what makes the backfill honest instead of a forged success.
 */
export const TriageEscalationDelivery = {
  /**
   * Settled, and the notice not yet attempted: what every settler that TELLS writes at
   * the settle (the list above, #7649). The pass whose settle won moves it on, once,
   * to one of the next two.
   */
  Pending: 'pending',
  /** `notify.fanout` accepted the event. The notification ledger owns reach from here. */
  Queued: 'queued',
  /** The enqueue faulted. Nobody will be told — the escalation reached no audience. */
  Undelivered: 'undelivered',
  /** Backfill only (`0180`): the row was settled before the escalate arm had a delivery leg. */
  Unattempted: 'unattempted',
} as const;
export const TriageEscalationDeliverySchema = z.enum(TriageEscalationDelivery);
export type TriageEscalationDelivery = z.infer<typeof TriageEscalationDeliverySchema>;

/**
 * The whole delivery vocabulary, flat — the source of truth the DB CHECK
 * (the `escalated` arm of `raw_tasks_lifecycle_ck`) is held to by SET EQUALITY in
 * `packages/db/src/schema.test.ts`. SQL cannot import an enum, so the constraint
 * restates this list; the set-equality pin is what stops the restatement drifting.
 * Adding a member here without widening the constraint fails the build rather than
 * an INSERT, and so does widening the constraint past what this declares.
 */
export const TRIAGE_ESCALATION_DELIVERIES = TriageEscalationDeliverySchema.options;

/**
 * What the delivery leg RECORDS once it has run: the two outcomes `pending` may move
 * to. `unattempted` is backfill only and `pending` is the settle's own, so neither is
 * spellable here.
 */
export type TriageEscalationOutcome =
  | typeof TriageEscalationDelivery.Queued
  | typeof TriageEscalationDelivery.Undelivered;

// ─── needs_info (plan 101 slice 08) ─────────────────────────────────────────

/**
 * How many questions one `needs_info` round may ask. A round is one message to
 * one person; past five it stops being a question and becomes a form nobody
 * fills in, and the round cap below then spends a whole round on it.
 */
export const RAW_TASK_INFO_QUESTIONS_MAX = 5;

/**
 * The cap on ONE question's text. It is posted verbatim — as a GitHub issue
 * comment for a `gh_issue` ask, and on the blocker every channel opens — so it is
 * a sentence a reporter reads, not a document. 500 matches the envelope's
 * acceptance-criterion cap, the other "one sentence a human acts on" field.
 */
export const RAW_TASK_INFO_QUESTION_MAX = 500;

/**
 * How many `needs_info` rounds one ask may spend before triage must decide
 * without the asker.
 *
 * THE FAILURE IT PREVENTS: an endless question loop. Every answer re-enters
 * triage (`awaiting_info -> pending`), and nothing else stops a model that
 * answers every answer with another question — the reporter is held in Q&A
 * forever and no maintainer is ever pulled in, which is the silence
 * {@link TriageEscalationDelivery} exists to end. At the cap the ask settles
 * `escalate` with {@link RawTaskInfoEscalationReason.RoundsExhausted}.
 *
 * THREE: one round to ask, one to follow up on a partial answer, one for the
 * misunderstanding a follow-up reveals. An ask still undecidable after three
 * answers from its own author is one a maintainer has to read.
 *
 * IT IS NOT A COST CAP — a round costs one batched triage share. It bounds a
 * loop with no other exit (`docs/standards/agent-budgets.md`).
 */
export const RAW_TASK_INFO_ROUNDS_MAX = 3;

/**
 * May the settle ask ANOTHER round? Takes `raw_tasks.info_rounds` — the rounds
 * already asked — so a count AT the cap means the cap is spent: the ask is
 * judged on its last answer, and a further `needs_info` settles `escalate`
 * `info_rounds_exhausted` instead.
 */
export function rawTaskInfoRoundsExhausted(infoRounds: number): boolean {
  return infoRounds >= RAW_TASK_INFO_ROUNDS_MAX;
}

/**
 * WHERE a `needs_info` round's questions were posted. The blocker exists on
 * BOTH (it is the answer door and the dashboard's view of the wait); the channel
 * says whether the asker was ALSO reached where they asked.
 */
export const RawTaskInfoChannel = {
  /** A disclosed top-level comment on the ask's GitHub issue, plus the blocker. */
  GhIssueComment: 'gh_issue_comment',
  /** The blocker alone — an ask whose inlet has no reply surface of its own. */
  Blocker: 'blocker',
} as const;
export const RawTaskInfoChannelSchema = z.enum(RawTaskInfoChannel);
export type RawTaskInfoChannel = z.infer<typeof RawTaskInfoChannelSchema>;

/**
 * The two `escalate` reasons that end an `awaiting_info` ask without an answer
 * that re-opened it. Machine-readable `reason` values on an ordinary `escalate`
 * verdict, named here so the writers (blocker expiry, the settle at the cap) and
 * any query spell them once.
 */
export const RawTaskInfoEscalationReason = {
  /** The question's blocker expired with no answer. */
  Unanswered: 'info_unanswered',
  /** Triage asked again with {@link RAW_TASK_INFO_ROUNDS_MAX} rounds already spent. */
  RoundsExhausted: 'info_rounds_exhausted',
} as const;
export type RawTaskInfoEscalationReason =
  (typeof RawTaskInfoEscalationReason)[keyof typeof RawTaskInfoEscalationReason];

/**
 * The first MACHINE-failure `escalate` reason (`RAW_TASK_MACHINE_FAILURE_REASONS` in
 * `./redrive`, which lists the rest): the judge could not answer, so the escalation was
 * never a decision about the ask. STORED VOCABULARY with no writer — #5110 made an
 * unreachable judge a `model_unavailable` hold instead of a terminal escalate; the rows
 * settled before it are what this names, and `rawTaskRedriveVerdict` re-drives them.
 *
 * The domain's own spelling on purpose: the writer was
 * `TriageRefusal.JudgeUnavailable` (`@developerz/agent`), and this package
 * imports nothing internal. The two literals are held together by a test
 * (`packages/agent/src/triage/judge-transient.test.ts`), so a rename on either
 * side goes red.
 */
export const RAW_TASK_JUDGE_UNAVAILABLE_REASON = 'judge_unavailable';

/**
 * The `escalate` reason the CLAIM writes on an ask whose STORED row no longer parses
 * against `RawTaskSchema` (#7601): the claim ends it at once (`escalated`, delivery
 * `undelivered` — nobody is told, so it is a live alarm), rather than hand it out and
 * let it throw the whole batch on every pass. Writer: `claimPendingRawTaskBatch`
 * (`@developerz/db`). The row's other columns stay EXACTLY as stored, so the original
 * is never lost.
 *
 * DELIBERATELY NOT in `RAW_TASK_MACHINE_FAILURE_REASONS`: a machine re-drive would put
 * the same unparseable row back in the queue, and the claim would end it again.
 */
export const RAW_TASK_ROW_INVALID_REASON = 'row_invalid';

/**
 * THE dedupe key of the blocker a `needs_info` round opens — `needs_info:<rtk>:<round>`.
 * Per ROUND, so a re-ask after an answer is a new blocker rather than the answered
 * one re-found. `round` is 1-based (the round being asked, `info_rounds + 1`) and
 * throws outside `1..RAW_TASK_INFO_ROUNDS_MAX`: a round the cap forbids has no
 * blocker to key.
 */
export function rawTaskNeedsInfoDedupeKey(rawTaskId: RawTaskId, round: number): string {
  if (!Number.isInteger(round) || round < 1 || round > RAW_TASK_INFO_ROUNDS_MAX) {
    throw new Error(
      `rawTaskNeedsInfoDedupeKey: round ${round} for ${rawTaskId} is outside 1..${RAW_TASK_INFO_ROUNDS_MAX} — the settle must escalate '${RawTaskInfoEscalationReason.RoundsExhausted}' at the cap, not open another blocker`,
    );
  }
  return `needs_info:${rawTaskId}:${round}`;
}

/**
 * The intake screen DISCARDED the ask (plan 101 slice 09, H3 #5393) — the one
 * {@link RawTaskVerdictSchema} member that is NOT a {@link TriageVerdict}.
 *
 * WHY NOT A `TriageVerdict` MEMBER: that enum is shared with the envelope-level
 * dispatch gate and the board scheduler, which judge work already scoped and can
 * never see an unscreened ask; a sixth member there would be a verdict half its
 * readers must refuse. The screen runs BEFORE triage, on the raw ask only, so its
 * settle is spelled here and nowhere else. `rawTaskStatusForVerdict` places it on
 * `dropped` — decided not to happen, terminal, no task minted, no reply.
 *
 * WHY NOT `drop-noise` WITH A PREFIXED REASON: that verdict means triage JUDGED the
 * ask as noise; this one means the ask was never judged, because its text was
 * refused as input. The reason is the closed {@link ScreenDiscardReasonSchema},
 * never free text, so "how many injections did we refuse" is a GROUP BY.
 */
export const RAW_TASK_DISCARDED_VERDICT = 'discarded';

/**
 * The ask was WITHDRAWN (#5713): the fact that filed it no longer holds — before triage
 * took it up, or (`issue_closed`) while it sat escalated on a machine failure, by
 * `withdrawEscalatedRawTask`. The two pending writers, both in `@developerz/db` `queries/raw-tasks-withdraw.ts`:
 * `withdrawPendingRawTask` on the `cron.base_red_ask` lane, for a base-red fix ask whose
 * red episode cleared while the ask was still pending; and
 * `withdrawPendingBabysitSplitAsks`, inside a babysit re-arm's transaction, for a PR's
 * still-pending `babysit_split` ask (#6081). `rawTaskStatusForVerdict` places it on
 * `dropped`.
 *
 * WHY NOT `drop-noise` or `discarded`: `drop-noise` means triage JUDGED the ask, and
 * `discarded` takes only the screen's refusal reasons; this ask was never judged and
 * its text was never refused. The word is `BlockerResolution.Withdrawn`'s (`../blocker.ts`)
 * ("another route cleared it"). The reason is closed ({@link RawTaskWithdrawReason}),
 * so "how many asks did a green base withdraw" is a GROUP BY.
 */
export const RAW_TASK_WITHDRAWN_VERDICT = 'withdrawn';

/** Why an ask was withdrawn. Closed: a new reason is a new member, never free text. */
export const RawTaskWithdrawReason = {
  /** The `repo_base_red` episode the ask was filed for cleared before triage claimed it. */
  BaseRedCleared: 'base_red_cleared',
  /**
   * A maintainer re-armed the PR's babysit (`rearmPullRequestBabysit`) while its
   * `babysit_split` ask was still unclaimed: the owner chose to keep the PR, so the split
   * that would close it `superseded_by_split` no longer holds (#6081).
   */
  BabysitRearmed: 'babysit_rearmed',
  /**
   * The GitHub issue an ask was filed from CLOSED while the ask sat `escalated` on a
   * machine failure: the machine re-drive would re-judge work nobody wants any more, so the
   * worker withdraws it instead (`withdrawEscalatedRawTask`, `@developerz/db`). The one
   * reason withdrawn from `escalated` rather than `pending`.
   */
  IssueClosed: 'issue_closed',
} as const;
export const RawTaskWithdrawReasonSchema = z.enum(RawTaskWithdrawReason);
export type RawTaskWithdrawReason = z.infer<typeof RawTaskWithdrawReasonSchema>;

export const RawTaskVerdictSchema = z.discriminatedUnion('verdict', [
  z.strictObject({
    verdict: z.literal(TriageVerdict.Schedule),
    /** Short machine-readable basis (`auto_schedule`, ...). Free text defeats aggregation. */
    reason: VerdictReasonSchema,
    /**
     * Whether the board this decision was taken against could support the claim
     * that the ask is new — see {@link TriageNovelty}. Required, so a `schedule`
     * cannot be recorded without stating which.
     */
    novelty: TriageNoveltySchema,
    /**
     * The tasks this raw task BECAME. Required, and non-empty, on this member and
     * unspellable on the other three — the fan-out (one ask, N repo-scoped tasks)
     * is what `schedule` MEANS, and it is the only place a settled row records it.
     *
     * "A schedule that names no work" is not a state with a meaning; it is a
     * decision nobody can follow. `docs/idea/triage.md` puts it plainly — the
     * output is real tasks, or no tasks — and no tasks is `drop-noise` or
     * `escalate`, both of which say so and neither of which is this. The field
     * shipped `.optional()` with zero writers and 77 production rows
     * (`bridge_passthrough`, 2026-08-25..27) took exactly that shape: settled
     * `schedule`, naming nothing, joinable to nothing.
     *
     * `settleRawTask` (`@developerz/db`) additionally VERIFIES each id against
     * `tasks.raw_task_id`, because jsonb carries no foreign key and this array
     * and that column are two spellings of one join.
     */
    task_ids: z
      .array(TaskIdSchema)
      .min(1)
      .max(RAW_TASK_TASK_IDS_MAX)
      // A fan-out that names the same task twice is a miscount of what the ask
      // became, and it is exactly the shape a model-authored verdict produces.
      .refine((ids) => new Set(ids).size === ids.length, 'task_ids must be unique'),
  }),
  z.strictObject({
    verdict: z.literal(TriageVerdict.Escalate),
    reason: VerdictReasonSchema,
    /**
     * What the escalation's delivery leg DID — see {@link TriageEscalationDelivery}.
     *
     * REQUIRED on this member and unspellable on the other three, the same shape
     * `novelty` takes on `schedule`: an `escalate` is the ONLY verdict that owes a
     * human an appearance, so it is the only one that can state whether one was
     * arranged. A `schedule` answers its asker in the work it minted, a `dupe-of` in
     * `NotificationKind.AskDuplicate`, and a `drop-noise` owes nothing.
     *
     * Optional was the alternative and it is the failure this closes: an omitted
     * field reads identically to an escalation nobody tried to deliver, which is
     * precisely the state all 47 production rows were in.
     */
    delivery: TriageEscalationDeliverySchema,
  }),
  z.strictObject({
    verdict: z.literal(TriageVerdict.DropNoise),
    reason: VerdictReasonSchema,
  }),
  z.strictObject({
    verdict: z.literal(TriageVerdict.DupeOf),
    reason: VerdictReasonSchema,
    /**
     * The in-motion task this ask duplicates. REQUIRED here and absent everywhere
     * else — without it the dropped ask has no join to the work that covers it,
     * and the trail says "dropped" with no way to find the survivor.
     */
    dupe_of_task_id: TaskIdSchema,
  }),
  z.strictObject({
    verdict: z.literal(TriageVerdict.NeedsInfo),
    /** Short machine-readable basis (`no_acceptance`, `no_repro`, ...). */
    reason: VerdictReasonSchema,
    /**
     * What the asker is asked, verbatim. At least one — a `needs_info` that asks
     * nothing parks an ask on an answer nobody can give — and at most
     * {@link RAW_TASK_INFO_QUESTIONS_MAX}, each capped.
     */
    questions: z
      .array(z.string().trim().min(1).max(RAW_TASK_INFO_QUESTION_MAX))
      .min(1)
      .max(RAW_TASK_INFO_QUESTIONS_MAX),
    /** Where the questions were posted — see {@link RawTaskInfoChannel}. */
    channel: RawTaskInfoChannelSchema,
    /**
     * The blocker carrying this round's questions (dedupe key
     * {@link rawTaskNeedsInfoDedupeKey}). REQUIRED: it is the answer door, and
     * the join `answerBlocker` uses to find the parked ask — a question with no
     * blocker is a wait nothing can end but the expiry.
     */
    blocker_id: BlockerIdSchema,
  }),
  z.strictObject({
    verdict: z.literal(RAW_TASK_DISCARDED_VERDICT),
    /** Which refusal — see {@link ScreenDiscardReasonSchema}. */
    reason: ScreenDiscardReasonSchema,
  }),
  z.strictObject({
    verdict: z.literal(RAW_TASK_WITHDRAWN_VERDICT),
    /** Which fact stopped holding — see {@link RawTaskWithdrawReason}. */
    reason: RawTaskWithdrawReasonSchema,
  }),
]);
export type RawTaskVerdict = z.infer<typeof RawTaskVerdictSchema>;

/**
 * Every verdict a `dropped` row carries — the source of truth the `dropped` arm of
 * `raw_tasks_lifecycle_ck` is held to by SET EQUALITY in
 * `packages/db/src/schema-tables.test.ts`, as {@link TRIAGE_ESCALATION_DELIVERIES} is
 * for the `escalated` arm.
 */
export const RAW_TASK_DROPPED_VERDICTS = [
  TriageVerdict.DropNoise,
  TriageVerdict.DupeOf,
  RAW_TASK_DISCARDED_VERDICT,
  RAW_TASK_WITHDRAWN_VERDICT,
] as const satisfies readonly RawTaskVerdict['verdict'][];

/**
 * EVERY {@link TriageVerdict} has a member above. A discriminated union is a
 * hand-written list, so nothing else forces it to keep up with the vocabulary —
 * and a fifth verdict silently missing a member would not fail to parse, it would
 * fail to parse AT RUNTIME, in production, on the first ask that used it.
 *
 * This resolves to `never` the moment the union stops covering the enum, and the
 * assignment below then fails the BUILD. It is the type-level twin of the `never`
 * guard on a total switch, which is what a `z.discriminatedUnion` cannot be.
 */
type EveryTriageVerdictHasAVerdictMember = TriageVerdict extends RawTaskVerdict['verdict']
  ? true
  : never;
const _everyTriageVerdictIsSpelled: EveryTriageVerdictHasAVerdictMember = true;
void _everyTriageVerdictIsSpelled;
